import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Autocomplete, Box, TextField, Typography } from "@mui/material";
import { eqlUsers } from "../../../../apis/apiEQL";
import { EGBasicSelect } from "../../../MainComponents/Select/EGBasicSelect";
import { useReportContext } from "../../../Report/ReportContext/useReportContext";
import { DATE_PRESETS, computePresetRange } from "../utils/datePresets";
import { ASSIGNED_USER_FIELD } from "../utils/queryFilters";

// Debounce for the server-side user search, and how many matches to list.
const USER_SEARCH_DELAY_MS = 300;
const USER_SEARCH_LIMIT = 50;

const userLabel = (u) =>
  u ? `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email : "";

// Catalog depth was moved next to each catalog in the controls pane (it is a
// per-catalog setting), so it no longer lives in this panel.

/**
 * Left panel: report filters that narrow the set fed into the pivot.
 *
 * Emits an EQL filter tree ({ AND: [...] }) rather than query params. Note the
 * filter-side notation: nested JSON is addressed with `__` here (status__current),
 * unlike `fields` / `group_by` which use a dot.
 *
 * The date range is driven by corporate presets (this/last month, this/last
 * quarter, YTD) plus a custom range. A preset resolves to created_at bounds;
 * "custom" reveals two date pickers.
 *
 * Catalog values are NOT filtered here: `filters` rejects `metadata[key]`, and
 * the catalog identifiers exposed by /reports/eql/catalogues/ (form-field keys)
 * do not line up with the report list's AdvancedSearch keys. Catalogs are
 * instead available as pivot dimensions, and can be narrowed with the pivot's
 * own Filters zone.
 *
 * Assigned user is not a report field either (it lives in Assignment), so it is
 * emitted as a marker condition that fetchPivotData resolves at query time —
 * see ASSIGNED_USER_FIELD in utils/queryFilters.
 */
export const ReportFilters = ({ loading, onApply }) => {
  const [t] = useTranslation("exports");
  const { statusOptions, severityOptions, kindOfEntryways } = useReportContext();

  const [status, setStatus] = useState("");
  const [severity, setSeverity] = useState("");
  const [entryway, setEntryway] = useState("");

  // Assigned user: the selected user object, plus the server-side search state.
  const [assignedUser, setAssignedUser] = useState(null);
  const [userQuery, setUserQuery] = useState("");
  const [userOptions, setUserOptions] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Tenants can have thousands of users, so search on the server as the user
  // types instead of loading them all. Runs once on mount with an empty query.
  useEffect(() => {
    let active = true;
    setLoadingUsers(true);
    const handle = setTimeout(async () => {
      try {
        const resp = await eqlUsers({
          search: userQuery.trim() || undefined,
          limit: USER_SEARCH_LIMIT,
        });
        if (active) setUserOptions(resp?.results || []);
      } catch (e) {
        console.error("ReportFilters: could not search users", e);
        if (active) setUserOptions([]);
      } finally {
        if (active) setLoadingUsers(false);
      }
    }, USER_SEARCH_DELAY_MS);
    return () => {
      active = false;
      clearTimeout(handle);
    };
  }, [userQuery]);

  // Keep the chosen user among the options even when the current search no
  // longer returns it; otherwise MUI flags the value as invalid.
  const userChoices =
    assignedUser && !userOptions.some((u) => u.id === assignedUser.id)
      ? [assignedUser, ...userOptions]
      : userOptions;

  // Period always has a selection: it defaults to (and never falls below) the
  // current month, so a query is never accidentally run over all time.
  const [datePreset, setDatePreset] = useState("this_month");
  const [customAfter, setCustomAfter] = useState("");
  const [customBefore, setCustomBefore] = useState("");

  // Prepending an "All" entry is cheap; the option arrays are rebuilt by
  // useReportContext on every render anyway, so memoizing buys nothing.
  const withAll = (options) => [{ value: "", label: t("builder.all") }, ...options];

  const statusOpts = withAll(statusOptions);
  const severityOpts = withAll(severityOptions);
  const entrywayOpts = withAll(kindOfEntryways);

  /** Resolves the active date range: preset -> computed, custom -> inputs. */
  const resolveDateRange = () => {
    if (datePreset === "custom")
      return { after: customAfter, before: customBefore };
    // Empty should never happen (Period always has a selection), but fall back
    // to the current month rather than an unbounded range just in case.
    const range = computePresetRange(datePreset || "this_month");
    return range || { after: "", before: "" };
  };

  /** @returns {Object|undefined} EQL filter tree, or undefined when unfiltered */
  const buildFilters = () => {
    const conditions = [];
    if (status)
      conditions.push({ field: "status__current", operator: "eq", value: status });
    if (severity)
      conditions.push({ field: "severity", operator: "eq", value: severity });
    if (entryway)
      conditions.push({ field: "entryway", operator: "eq", value: entryway });
    if (assignedUser)
      conditions.push({
        field: ASSIGNED_USER_FIELD,
        operator: "eq",
        value: assignedUser.id,
      });

    const { after, before } = resolveDateRange();
    if (after)
      conditions.push({ field: "created_at", operator: "gte", value: after });
    if (before)
      conditions.push({ field: "created_at", operator: "lte", value: before });

    return conditions.length ? { AND: conditions } : undefined;
  };

  const handleClear = () => {
    setStatus("");
    setSeverity("");
    setEntryway("");
    setAssignedUser(null);
    setUserQuery("");
    setDatePreset("this_month"); // Period resets to the current month, not empty
    setCustomAfter("");
    setCustomBefore("");
  };

  // Presets act like radio buttons: one is always active. Re-clicking the
  // active preset keeps it, so Period is never left empty.
  const togglePreset = (key) => setDatePreset(key);

  return (
    <Box className="builder-panel builder-filterpanel dyTheme1 dyBorder1 rounded">
      <Typography className="builder-panel__title">
        {t("builder.filters_title")}
      </Typography>

      {/* Attributes */}
      <section className="builder-fsection">
        <Typography className="builder-fsection__label">
          {t("builder.section_attributes")}
        </Typography>
        <div className="builder-fsection__body">
          <EGBasicSelect
            label={t("builder.f_status")}
            value={status}
            options={statusOpts}
            onChange={(e) => setStatus(e.target.value)}
          />
          <EGBasicSelect
            label={t("builder.f_severity")}
            value={severity}
            options={severityOpts}
            onChange={(e) => setSeverity(e.target.value)}
          />
          <EGBasicSelect
            label={t("builder.f_channel")}
            value={entryway}
            options={entrywayOpts}
            onChange={(e) => setEntryway(e.target.value)}
          />
          <Autocomplete
            size="small"
            options={userChoices}
            value={assignedUser}
            onChange={(_, user) => setAssignedUser(user)}
            onInputChange={(_, text, reason) => {
              // "reset" fires when an option is picked; only real typing searches.
              if (reason === "input" || reason === "clear") setUserQuery(text);
            }}
            // Matching already happened on the server.
            filterOptions={(options) => options}
            getOptionLabel={userLabel}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={loadingUsers}
            loadingText={t("builder.loading")}
            noOptionsText={t("builder.assigned_user_none")}
            renderOption={(props, user) => {
              const { key, ...optionProps } = props;
              return (
                <li key={user.id} {...optionProps}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography fontSize={14} noWrap>
                      {userLabel(user)}
                    </Typography>
                    <Typography fontSize={12} color="text.secondary" noWrap>
                      {user.email}
                    </Typography>
                  </Box>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("builder.f_assigned_user")}
                placeholder={t("builder.assigned_user_search")}
              />
            )}
          />
        </div>
      </section>

      {/* Date range */}
      <section className="builder-fsection">
        <Typography className="builder-fsection__label">
          {t("builder.section_period")}
        </Typography>
        <div className="builder-presets">
          {DATE_PRESETS.map((key) => (
            <button
              key={key}
              type="button"
              className={`builder-preset ${
                datePreset === key ? "--active" : ""
              }`}
              onClick={() => togglePreset(key)}
              aria-pressed={datePreset === key}
            >
              {t(`builder.preset_${key}`)}
            </button>
          ))}
        </div>

        {datePreset === "custom" && (
          <div className="builder-fsection__body builder-daterange">
            <label className="builder-datefield">
              <span>{t("builder.date_from")}</span>
              <input
                type="date"
                className="form-control"
                value={customAfter}
                max={customBefore || undefined}
                onChange={(e) => setCustomAfter(e.target.value)}
              />
            </label>
            <label className="builder-datefield">
              <span>{t("builder.date_to")}</span>
              <input
                type="date"
                className="form-control"
                value={customBefore}
                min={customAfter || undefined}
                onChange={(e) => setCustomBefore(e.target.value)}
              />
            </label>
          </div>
        )}
      </section>

      <div className="builder-filters__actions">
        <button
          type="button"
          className="builder-btn builder-btn--ghost"
          onClick={handleClear}
          disabled={loading}
        >
          {t("builder.clear")}
        </button>
        <button
          type="button"
          className="builder-btn builder-btn--primary"
          onClick={() => onApply(buildFilters())}
          disabled={loading}
        >
          {loading ? t("builder.loading") : t("builder.apply")}
        </button>
      </div>
    </Box>
  );
};
